import { decodeTopic, encodeJson, ATTR, HOST_IDENTITY, type ChatV1, type SysV1, type InputV1, type HandV1, type TypingV1, type DisplayInfo } from '@gonzovnc/protocol';
import type { RoomTransport, RemoteParticipant } from './types.js';
import { InputEncoder, type Outgoing } from './input-encoder.js';
import { Coalescer } from './coalescer.js';
import { ControlClient } from './control-client.js';

export interface SessionCallbacks {
  onChat?: (msg: { name: string; body: string; ts: number; from: string; id: string }) => void;
  onRoster?: (participants: RemoteParticipant[]) => void;
  onControl?: (target: string | null, by?: string) => void;
  onPrivacy?: (on: boolean, reason: string) => void;
  onEnding?: (inSec: number, reason: string) => void;
  onKicked?: (target: string) => void;
  onCaptureEnabled?: (displays: DisplayInfo[]) => void;
  onCaptureDisabled?: (reason: string) => void;
  onHand?: (from: string, up: boolean, reason: 'speak' | 'control') => void;
  onTyping?: (from: string, typing: boolean) => void;
  onRoleChanged?: (role: string) => void;
  onError?: (code: string, msg?: string) => void;
  onInputRtt?: (rttMs: number) => void;
}

/**
 * DOM-free session hub (§6.9): decodes inbound data messages, drives the control
 * client from Host `hello`/`bye` and attribute changes, and exposes input send methods
 * fed by the InputEncoder + Coalescer. The browser adapter owns DOM events and the
 * animation-frame tick; this class owns all protocol logic.
 */
export class Session {
  readonly encoder = new InputEncoder();
  readonly control: ControlClient;
  readonly coalescer: Coalescer;
  private unsub: Array<() => void> = [];
  private rejected = 0;

  constructor(
    private transport: RoomTransport,
    private cb: SessionCallbacks = {},
  ) {
    this.control = new ControlClient(transport.localIdentity(), this.encoder, {
      onCaptureEnabled: (d) => this.cb.onCaptureEnabled?.(d),
      onCaptureDisabled: (r) => this.cb.onCaptureDisabled?.(r),
      onRoleChanged: (role) => this.cb.onRoleChanged?.(role),
    });
    this.coalescer = new Coalescer(this.encoder, (o) => this.send(o));
    this.wire();
  }

  get rejectedCount(): number { return this.rejected; }

  private wire(): void {
    this.unsub.push(this.transport.onData((topic, payload, from) => this.handleData(topic, payload, from)));
    this.unsub.push(
      this.transport.onAttributesChanged((identity, attrs) => {
        if (identity === this.transport.localIdentity()) this.control.onMyAttributes(attrs);
        this.cb.onRoster?.(this.transport.participants());
      }),
    );
    this.unsub.push(
      this.transport.onParticipant(() => {
        this.cb.onRoster?.(this.transport.participants());
      }),
    );
    this.unsub.push(
      this.transport.onRoomMetadata((meta) => {
        try {
          const parsed = JSON.parse(meta) as { private_control?: boolean };
          if (typeof parsed.private_control === 'boolean') this.cb.onPrivacy?.(parsed.private_control, 'admin');
        } catch {
          /* ignore non-JSON metadata */
        }
      }),
    );
  }

  private handleData(topic: string, payload: Uint8Array, from: string): void {
    switch (topic) {
      case 'chat.v1': {
        const r = decodeTopic('chat.v1', payload);
        if (!r.ok) { this.rejected++; return; }
        const m = r.value as ChatV1;
        const name = this.transport.participants().find((p) => p.identity === from)?.name ?? from;
        this.cb.onChat?.({ name, body: m.body, ts: m.ts, from, id: m.id });
        break;
      }
      case 'sys.v1': {
        const r = decodeTopic('sys.v1', payload);
        if (!r.ok) { this.rejected++; return; }
        this.handleSys(r.value as SysV1);
        break;
      }
      case 'hand.v1': {
        const r = decodeTopic('hand.v1', payload);
        if (!r.ok) { this.rejected++; return; }
        const m = r.value as HandV1;
        this.cb.onHand?.(from, m.state === 'up', m.reason);
        break;
      }
      case 'typing.v1': {
        const r = decodeTopic('typing.v1', payload);
        if (!r.ok) { this.rejected++; return; }
        const m = r.value as TypingV1;
        this.cb.onTyping?.(from, m.state === 'start');
        break;
      }
      case 'input.v1': {
        // Only Host->controller replies are accepted, and only from the reserved host identity.
        if (from !== HOST_IDENTITY) { this.rejected++; return; }
        const r = decodeTopic('input.v1', payload);
        if (!r.ok) { this.rejected++; return; }
        this.handleHostInput(r.value as InputV1);
        break;
      }
      default:
        break;
    }
  }

  private handleSys(m: SysV1): void {
    switch (m.t) {
      case 'control': this.cb.onControl?.(m.target, m.by); break;
      case 'privacy': this.cb.onPrivacy?.(m.on, m.reason); break;
      case 'ending': this.cb.onEnding?.(m.inSec, m.reason); break;
      case 'kicked': this.cb.onKicked?.(m.target); break;
      case 'error': this.cb.onError?.(m.code, m.msg); break;
      default: break;
    }
  }

  private handleHostInput(m: InputV1): void {
    switch (m.t) {
      case 'hello': this.control.onHello(m.e, m.displays); break;
      case 'displays': this.cb.onCaptureEnabled?.(m.displays); break;
      case 'bye': this.control.onBye(m.reason); break;
      case 'pong': this.cb.onInputRtt?.(Date.now() - m.ts); break;
      case 'err': this.cb.onError?.(m.code); break;
      default: break;
    }
  }

  /** Publish an encoder Outgoing to the Host, respecting reliability per topic. */
  send(o: Outgoing): void {
    if (!this.control.captureEnabled) return;
    this.transport.publishData(o.topic, encodeJson(o.message), {
      reliable: o.topic === 'input.v1',
      destinationIdentities: [HOST_IDENTITY],
    });
  }

  /** Send chat: broadcast on chat.v1 and persist via REST is the adapter's job. */
  sendChat(msg: ChatV1): void {
    this.transport.publishData('chat.v1', encodeJson(msg), { reliable: true });
  }

  dispose(): void {
    for (const u of this.unsub) u();
    this.unsub = [];
  }
}
